import { useEffect, useRef, useState } from 'react';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, CalendarClock } from 'lucide-react';
import { isInFlight, type RecordStatus } from '@tabservice/tabfile-client';
import { Button } from '@/components/ui/button';
import { Card, SectionHeader } from '@/components/ui/card';
import { CopyValue } from '@/components/ui/copy';
import { DetailSkeleton, EmptyState, ErrorNote, StatsSkeleton, TableSkeleton } from '@/components/ui/feedback';
import { Combobox } from '@/components/ui/combobox';
import { TablePagination, ToolbarField } from '@/components/ui/table';
import { useSetPageTitle } from '@/components/page-title';
import { FilingStatusPill } from '@/features/filings/status-pill';
import { FilingActions, FilingLineage } from '@/features/filings/filing-actions';
import { FilingTimeline } from '@/features/filings/filing-timeline';
import { RecordsTable } from '@/features/filings/records-table';
import { RecordModal } from '@/features/filings/record-modal';
import { SubmitQueuedFixes } from '@/features/filings/submit-queued';
import {
  fixFilterOptions,
  fixStateOf,
  fixSummaryLine,
  needsAttention,
  type FixFilter,
} from '@/features/filings/record-fix';
import { ErrorReasons, shouldExplainFailures } from '@/features/filings/error-reasons';
import { TransmissionsTable } from '@/features/filings/transmissions-table';
import { filingKindLabel, filingStatusMeta } from '@/features/filings/status';
import { waitingLine } from '@/features/filings/waiting';
import { filingFormatTag } from '@/features/new-filing/formats';
import { filingQuery, filingRecordsPageQuery, queryKeys } from '@/lib/queries';
import { useTaxYear } from '@/lib/tax-year';
import { modeCopy } from '@/lib/mode';
import { errorMessage } from '@/lib/api';
import { formatCount, formatDateTime } from '@/lib/format';
import { cn } from '@/lib/cn';

export const Route = createFileRoute('/_app/e-files/$efileId')({
  // The shape this page takes, rather than a search box over a table for
  // every route: the screen must not rearrange itself once the data lands.
  pendingComponent: () => <DetailSkeleton />,
  // The e-file and its first page of records, together: the page shows both, and asking
  // one after the other doubled the wait against a remote database.
  loader: ({ context, params }) =>
    Promise.all([
      context.queryClient.ensureQueryData(filingQuery(params.efileId)),
      context.queryClient.ensureQueryData(filingRecordsPageQuery(params.efileId, undefined, undefined, 1, PAGE_SIZE)),
    ]).then(([filing]) => filing),
  component: FilingDetailPage,
});

const recordStatusOptions = [
  { value: 'all', label: 'All records' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'error', label: 'Accepted with errors' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'transmitted', label: 'Transmitted' },
  { value: 'pending', label: 'Pending' },
];

const PAGE_SIZE = 10;

/** One fact in the summary grid: small grey label, value under it. */
function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="mt-1 truncate text-base text-gray-3">{children}</dd>
    </div>
  );
}

/**
 * A count worth reading at a glance. Numbers line up, so they compare.
 *
 * `awaiting` is for the counts the IRS has not decided yet. Nought accepted and nought with
 * errors are true of an e-file queued a minute ago and of one the IRS threw out entirely, and
 * on the page somebody opens to find out which, printing the same two noughts for both is the
 * one answer worth avoiding.
 */
function Stat({
  label,
  value,
  tone = 'plain',
  awaiting = false,
}: {
  label: string;
  value: number;
  tone?: 'plain' | 'warning';
  awaiting?: boolean;
}) {
  return (
    <div>
      <p
        className={cn(
          'text-[22px] leading-7 font-semibold tabular-nums',
          awaiting
            ? 'text-muted-foreground'
            : tone === 'warning' && value > 0
              ? 'text-[color:var(--ts-pill-warning-text)]'
              : 'text-primary',
        )}
        title={awaiting ? 'The IRS has not answered yet' : undefined}
      >
        {awaiting ? '—' : formatCount(value)}
      </p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  );
}

function FilingDetailPage() {
  const { efileId: filingId } = Route.useParams();
  const { taxYear, setTaxYear } = useTaxYear();
  const navigate = useNavigate();

  const filing = useQuery({
    ...filingQuery(filingId),
    refetchInterval: (item) => (item.state.data && isInFlight(item.state.data.status) ? 15_000 : false),
  });

  const data = filing.data;
  useSetPageTitle(data ? `${data.formType}, tax year ${data.taxYear}` : null);

  const [recordStatus, setRecordStatus] = useState<'all' | RecordStatus>('all');
  /**
   * The records table opens on the records that need somebody, when there are any.
   *
   * Two and a half thousand records with twenty-three errors in them is two hundred and forty
   * pages, and the twenty-three are the reason the page was opened at all. The filter is a
   * normal control and says "Needs attention" while it is on, so nothing is hidden - the table
   * just starts where the work is. An e-file with nothing outstanding opens on everything,
   * because then everything is the answer.
   */
  const [fixFilter, setFixFilter] = useState<FixFilter>(() =>
    (filing.data?.attentionCount ?? 0) > 0 ? 'attention' : 'all',
  );
  const [page, setPage] = useState(1);
  const [openRecordRef, setOpenRecordRef] = useState<string | null>(null);
  /** One reason out of the summary above the table, or every reason. */
  const [errorCode, setErrorCode] = useState<string | null>(null);

  // Moving from one e-file to the next re-decides it. The route may keep this component alive
  // across that move, and the previous e-file's filter would otherwise come along.
  const settledFor = useRef(filingId);
  useEffect(() => {
    if (settledFor.current === filingId) return;
    settledFor.current = filingId;
    setFixFilter((filing.data?.attentionCount ?? 0) > 0 ? 'attention' : 'all');
    setRecordStatus('all');
    setErrorCode(null);
    setPage(1);
  }, [filingId, filing.data?.attentionCount]);

  /**
   * Choosing a reason widens the table to every record with it, whatever state their fixes
   * are in. "Show me the eighteen" and "show me what still needs me" are different questions,
   * and answering the first through the second's filter would show four of the eighteen and
   * leave somebody counting.
   */
  function chooseErrorCode(code: string | null) {
    setErrorCode(code);
    if (code) {
      setFixFilter('all');
      setRecordStatus('all');
    }
    setPage(1);
  }

  // Both filters are Tab Service's: the IRS status and the fix state (including "needs
  // attention"), and so is the page, so page 5,500 of sixty thousand records is one request.
  //
  // The records move with the e-file: each one gets its IRS result when the acknowledgement
  // comes back, the same moment the status above changes. Only the status used to poll, so the
  // header said Accepted while every row still said Pending until the page was reloaded.
  const inFlight = !!filing.data && isInFlight(filing.data.status);
  const records = useQuery({
    ...filingRecordsPageQuery(
      filingId,
      recordStatus === 'all' ? undefined : recordStatus,
      fixFilter === 'all' ? undefined : fixFilter,
      page,
      PAGE_SIZE,
      errorCode ?? undefined,
    ),
    refetchInterval: inFlight ? 15_000 : false,
  });

  // The poll that brings the final status is the last one, and the records it raced may still
  // be the old ones. When the status changes, read every page of records again.
  const queryClient = useQueryClient();
  const seenStatus = useRef(filing.data?.status);
  useEffect(() => {
    const status = filing.data?.status;
    const previous = seenStatus.current;
    seenStatus.current = status;
    if (previous && status && previous !== status) {
      void queryClient.invalidateQueries({ queryKey: queryKeys.filingRecords(filingId).slice(0, 2) });
    }
  }, [filing.data?.status, filingId, queryClient]);
  const rows = records.data?.items ?? [];
  const recordTotal = records.data?.total ?? 0;

  if (filing.isError) {
    return (
      <Card>
        <ErrorNote
          title="Could not load this e-file"
          detail={errorMessage(filing.error)}
          action={
            <Button variant="outline" size="sm" onClick={() => void filing.refetch()}>
              Try again
            </Button>
          }
        />
      </Card>
    );
  }

  const otherYear = data && data.taxYear !== taxYear ? data.taxYear : null;

  const attentionCount = data?.attentionCount ?? rows.filter(needsAttention).length;
  const queuedCount = data?.queuedFixes ?? rows.filter((record) => fixStateOf(record) === 'queued').length;
  const summaryLine = fixSummaryLine(attentionCount, queuedCount);

  return (
    <>
      <div className="flex flex-wrap items-center gap-3">
        <Button asChild variant="ghost" size="sm" className="-ml-3">
          <Link to="/e-files">
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            All e-files
          </Link>
        </Button>

        {otherYear ? (
          <p
            className={cn(
              'flex flex-wrap items-center gap-2 rounded-lg border border-[color:var(--ts-pill-info-border)]',
              'bg-[color:var(--ts-pill-info-bg)] px-3 py-1.5 text-sm text-primary',
            )}
          >
            <CalendarClock className="h-4 w-4 shrink-0" aria-hidden="true" />
            This filing is for tax year {otherYear}.
            <button
              type="button"
              onClick={() => setTaxYear(otherYear)}
              className="font-medium underline underline-offset-2"
            >
              Switch to {otherYear}
            </button>
          </p>
        ) : null}
      </div>

      <div className="grid items-start gap-6 min-[1100px]:grid-cols-[minmax(0,1fr)_320px]">
        <div className="flex min-w-0 flex-col gap-6">
          <Card data-tour="e-file-summary">
            {/*
              While an e-file is still moving, the description says what it is waiting for and
              when that wait ends, rather than restating the pill beside it.
            */}
            <SectionHeader
              title="Summary"
              description={data ? (waitingLine(data) ?? filingStatusMeta[data.status].hint) : undefined}
              actions={
                data ? (
                  <>
                    <SubmitQueuedFixes
                      filing={data}
                      records={rows}
                      onSubmitted={(created) =>
                        void navigate({ to: '/e-files/$efileId', params: { efileId: created.id } })
                      }
                    />
                    <FilingActions filing={data} />
                  </>
                ) : null
              }
            />
            {data ? (
              <>
                <dl className="grid grid-cols-2 gap-x-6 gap-y-5 sm:grid-cols-3">
                  <Fact label="Status">
                    <FilingStatusPill status={data.status} />
                  </Fact>
                  <Fact label="Form">{data.formType}</Fact>
                  <Fact label="Tax year">{data.taxYear}</Fact>
                  <Fact label="Kind">{filingKindLabel[data.kind]}</Fact>
                  <Fact label="Sent as">
                    {data.format ? (filingFormatTag[data.format] ?? data.format) : 'JSON'}
                  </Fact>
                  <Fact label="Mode">{modeCopy[data.mode].label}</Fact>
                </dl>
                <div className="mt-6 flex flex-wrap gap-x-12 gap-y-4 border-t border-[color:var(--ts-row-border)] pt-5">
                  <Stat label="Records" value={data.recordCount} />
                  <Stat label="Accepted" value={data.acceptedCount ?? 0} awaiting={isInFlight(data.status)} />
                  <Stat
                    label="With errors"
                    value={data.errorCount ?? 0}
                    tone="warning"
                    awaiting={isInFlight(data.status)}
                  />
                </div>
                {data.parentEfileId || (data.childEfileIds?.length ?? 0) > 0 ? (
                  <div className="mt-6 border-t border-[color:var(--ts-row-border)] pt-5">
                    <FilingLineage filing={data} />
                  </div>
                ) : null}
              </>
            ) : (
              // Facts and figures, not rows: a table skeleton here drew a shape the card
              // never takes, so the page changed layout as it finished loading.
              <>
                <StatsSkeleton count={6} />
                <div className="mt-6 border-t border-[color:var(--ts-row-border)] pt-5">
                  <StatsSkeleton count={3} />
                </div>
              </>
            )}
          </Card>

          <Card data-tour="transmissions">
            <SectionHeader
              title="Transmissions"
              description={
                data?.format === 'irisxml'
                  ? "Sent to the IRS as a single transmission, under Tab Service's transmitter header."
                  : 'Each chunk is one file sent to the IRS. Download the XML for the full detail.'
              }
            />
            {data ? (
              data.transmissions.length === 0 ? (
                <p className="py-4 text-base text-muted-foreground">
                  The records are being prepared. Transmissions appear here as they are written, within seconds for
                  a small e-file and a minute or two for a large one. This page updates by itself.
                </p>
              ) : (
                <TransmissionsTable filingId={filingId} items={data.transmissions} />
              )
            ) : (
              <TableSkeleton rows={3} columns={6} />
            )}
          </Card>

          {/*
            Above the table, because it is the shape of the problem and the table is the
            problem itself. An e-file the IRS was happy with renders nothing here.
          */}
          <ErrorReasons
            filingId={filingId}
            /*
              Anything the IRS refused, however it refused it. Gating on errorCount alone
              hid this panel exactly when it was needed most: a transmission rejected as a
              whole leaves every record rejected and something to explain, and a customer
              was then left opening forty drawers to read one sentence forty times.

              Loose on purpose - the panel renders nothing when there is nothing to group,
              so asking too often costs one request and shows no empty card.
            */
            enabled={shouldExplainFailures({ ...data, attentionCount })}
            selectedCode={errorCode}
            onSelect={chooseErrorCode}
          />

          <Card data-tour="records">
            <SectionHeader
              title="Records"
              description="Open a row to read the IRS result, edit the record and queue a fix."
              actions={
                <>
                  <ToolbarField label="Status" className="w-[220px]">
                    <Combobox
                      ariaLabel="Filter records by status"
                      value={recordStatus}
                      onValueChange={(value) => {
                        setRecordStatus(value as 'all' | RecordStatus);
                        setPage(1);
                      }}
                      options={recordStatusOptions}
                    />
                  </ToolbarField>
                  <ToolbarField label="Fix" className="w-[190px]">
                    <Combobox
                      ariaLabel="Filter records by fix state"
                      value={fixFilter}
                      onValueChange={(value) => {
                        setFixFilter(value as FixFilter);
                        setPage(1);
                      }}
                      options={fixFilterOptions}
                    />
                  </ToolbarField>
                </>
              }
            />
            {errorCode ? (
              <p className="-mt-2 flex flex-wrap items-center gap-2 pb-4 text-base text-muted-foreground">
                Showing the records with
                <span className="font-mono text-sm text-gray-3">{errorCode}</span>
                <button
                  type="button"
                  onClick={() => chooseErrorCode(null)}
                  className="font-medium text-primary underline-offset-4 hover:underline"
                >
                  Show all records
                </button>
              </p>
            ) : summaryLine ? (
              <p className="-mt-2 pb-4 text-base text-muted-foreground">{summaryLine}</p>
            ) : null}
            {records.isPending ? (
              <TableSkeleton rows={6} columns={6} />
            ) : records.isError ? (
              <ErrorNote title="Could not load records" detail={errorMessage(records.error)} />
            ) : rows.length === 0 ? (
              <EmptyState title="No records" description="No records on this filing match those filters." />
            ) : (
              <>
                <RecordsTable records={rows} onOpen={setOpenRecordRef} />
                <TablePagination page={page} pageSize={PAGE_SIZE} total={recordTotal} onPageChange={setPage} />
              </>
            )}
          </Card>
        </div>

        <div className="flex flex-col gap-6">
          <Card data-tour="timeline">
            <SectionHeader title="Activity" />
            <FilingTimeline
              entries={data?.timeline ?? []}
              inFlight={data ? isInFlight(data.status) : false}
              waiting={data ? waitingLine(data) : null}
            />
          </Card>

          <Card>
            <SectionHeader title="Details" />
            <dl className="flex flex-col gap-4">
              <Fact label="E-file id">
                {data ? <CopyValue value={data.id} label="e-file id" /> : '-'}
              </Fact>
              <Fact label="Created">{formatDateTime(data?.createdAt)}</Fact>
              <Fact label="Last update">{formatDateTime(data?.updatedAt)}</Fact>
              {/* The name on the customer's own machine: the one thing here they chose. */}
              {data?.fileName ? <Fact label="File">{data.fileName}</Fact> : null}
              {data?.idempotencyKey && !data.idempotencyKey.startsWith('web-') ? (
                <Fact label="Your reference">{data.idempotencyKey}</Fact>
              ) : null}
            </dl>
          </Card>
        </div>
      </div>

      <RecordModal
        filingId={filingId}
        formType={data?.formType}
        taxYear={data?.taxYear}
        format={data?.format}
        recordRef={openRecordRef}
        onClose={() => setOpenRecordRef(null)}
      />
    </>
  );
}
